/**
 * DataTable — shared table wrapper (SF-3733).
 *
 * A thin, opinionated wrapper over antd's Table that standardizes the loading
 * skeleton, empty state, pagination defaults, and row-click navigation used
 * across Eva list views. Generic over the row type so callers keep full column
 * typing. Later Eva pages (company/user lists) reuse this directly.
 */
import { Table, type TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';

/** Default page size for Eva tables (server-paginated lists override as needed). */
export const DEFAULT_PAGE_SIZE = 20;

export interface DataTableProps<T> {
  /** Row data. */
  data: T[];
  /** Column definitions. */
  columns: ColumnsType<T>;
  /** Whether the underlying query is loading. */
  loading?: boolean;
  /** Stable row key extractor. */
  rowKey: (record: T, index?: number) => string;
  /**
   * Pagination config. Pass `false` to disable, or a partial config to override
   * the defaults. When omitted, client-side pagination with DEFAULT_PAGE_SIZE
   * is used.
   */
  pagination?: TablePaginationConfig | false;
  /** Optional row-click handler (adds a pointer cursor). */
  onRowClick?: (record: T) => void;
  /** Optional empty-state text. */
  emptyText?: string;
  /** Table size (default 'middle'). */
  size?: 'small' | 'middle' | 'large';
}

/** Reusable, generically-typed table for Eva list views. */
export function DataTable<T extends object>({
  data,
  columns,
  loading = false,
  rowKey,
  pagination,
  onRowClick,
  emptyText,
  size = 'middle',
}: DataTableProps<T>) {
  const resolvedPagination: TablePaginationConfig | false =
    pagination === false
      ? false
      : {
          pageSize: DEFAULT_PAGE_SIZE,
          showSizeChanger: false,
          hideOnSinglePage: true,
          ...pagination,
        };

  return (
    <Table<T>
      dataSource={data ?? []}
      columns={columns}
      loading={loading}
      rowKey={rowKey}
      size={size}
      pagination={resolvedPagination}
      // While loading, show the spinner only: the empty text would claim there is no data yet.
      locale={emptyText ? { emptyText: loading ? <span /> : emptyText } : undefined}
      onRow={
        onRowClick
          ? (record) => ({
              onClick: () => onRowClick(record),
              style: { cursor: 'pointer' },
            })
          : undefined
      }
    />
  );
}
